import React, { useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  CardHeader,
  Button,
  Badge,
  FormGroup,
  Input,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  useGetStockManagementByIdQuery,
  useGetProjectTransferCapacityQuery,
  useCreateStockProjectAllocationMutation,
  useUpdateStockManagementMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { IsValidPermissions } from "Routes/AuthProtected";

interface Allocation {
  id: number;
  stock_management: number;
  project: number;
  project_name: string;
  target_project_name?: string;
  product_id: number;
  product_name: string;
  supplier_name: string;
  stock_reference_number: string | null;
  quantity: string;
  allocation_type: string;
  allocation_type_display: string;
  purchase_order_number?: string | null;
  transfer_status?: string;
  allocation_no?: string;
  allocated_date: string;
  transferred_date: string;
  performed_by: number;
  performed_by_name: string;
  notes: string;
  created_at: string;
  modified_at: string;
}

interface StockManagementDetail {
  id: number;
  source_pr_item: number;
  source_purchase_request: number;
  product: number;
  product_name: string;
  supplier: number;
  supplier_name: string;
  actual_quantity: string;
  balance_quantity: string;
  unit_price: string;
  total_cost: string;
  currency: string | null;
  stock_reference_number: string | null;
  status: string;
  status_display: string;
  stock_type: string;
  stock_type_display: string;
  source_project: number;
  source_project_name: string;
  source_purchase_request_number: string;
  consumed_date: string | null;
  notes: string;
  allocations: Allocation[];
  attachments?: {
    id: number;
    name: string | null;
    file_url: string | null;
    created_at: string;
  }[];
  created_at: string;
  modified_at: string;
}

const StockManagementDetailView = () => {
  const { stockId } = useParams();
  const navigate = useNavigate();

  // API hooks
  const {
    data: stockDetail,
    isLoading,
    error,
    refetch,
  } = useGetStockManagementByIdQuery(stockId);

  const [createAllocation, { isLoading: isCreatingAllocation }] =
    useCreateStockProjectAllocationMutation();

  const [updateStock, { isLoading: isUpdatingStock }] =
    useUpdateStockManagementMutation();

  // State
  const [activeTab, setActiveTab] = useState("1");
  const [allocateModal, setAllocateModal] = useState(false);
  const [updateNotesModal, setUpdateNotesModal] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);

  const [allocationForm, setAllocationForm] = useState({
    project: "",
    quantity: "",
    allocation_type: "allocated",
    allocated_date: new Date().toISOString().split("T")[0],
    transferred_date: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [notesForm, setNotesForm] = useState({
    notes: "",
  });

  const selectedProjectId = allocationForm.project;
  const {
    data: projectCapacity,
    isFetching: isLoadingProjectCapacity,
    error: projectCapacityError,
  } = useGetProjectTransferCapacityQuery(
    {
      project_id: selectedProjectId,
      product_id: stockDetail?.product || "",
    },
    { skip: !selectedProjectId || !stockDetail?.product }
  );
  const sourceBalance = Number(stockDetail?.balance_quantity || 0);
  const targetRemainingQuantity = Number(
    (projectCapacity as any)?.remaining_quantity
  );
  const hasTargetCapacity = Number.isFinite(targetRemainingQuantity);
  const maximumTransferQuantity = hasTargetCapacity
    ? Math.min(sourceBalance, targetRemainingQuantity)
    : sourceBalance;

  // Initialize notes form when stock detail loads
  React.useEffect(() => {
    if (stockDetail) {
      setNotesForm({ notes: stockDetail.notes || "" });
    }
  }, [stockDetail]);

  // Handlers
  const toggleAllocateModal = () => {
    setAllocateModal(!allocateModal);
    if (!allocateModal) {
      // Reset form
      setAllocationForm({
        project: "",
        quantity: "",
        allocation_type: "allocated",
        allocated_date: new Date().toISOString().split("T")[0],
        transferred_date: new Date().toISOString().split("T")[0],
        notes: "",
      });
    }
  };

  const toggleUpdateNotesModal = () => {
    setUpdateNotesModal(!updateNotesModal);
  };

  const toggleActionDropdown = () => {
    setActionDropdownOpen(!actionDropdownOpen);
  };

  const handleAllocationInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setAllocationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNotesInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNotesForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateAllocation = async () => {
    if (!stockId) return;

    const quantity = parseFloat(allocationForm.quantity);
    const balanceQuantity = parseFloat(stockDetail?.balance_quantity || "0");

    if (quantity <= 0) {
      alert("Please enter a valid quantity");
      return;
    }

    if (quantity > balanceQuantity) {
      alert(`Cannot allocate more than available balance: ${balanceQuantity}`);
      return;
    }

    if (!allocationForm.project) {
      alert("Please select a project");
      return;
    }

    if (projectCapacityError) {
      const message =
        (projectCapacityError as any)?.data?.error ||
        "This product is not assigned and published for the target project.";
      alert(message);
      return;
    }

    const remainingProjectQuantity = Number(
      (projectCapacity as any)?.remaining_quantity
    );
    if (
      Number.isFinite(remainingProjectQuantity) &&
      quantity > remainingProjectQuantity
    ) {
      alert(
        `Transfer quantity exceeds the target project's remaining quantity: ${remainingProjectQuantity}.`
      );
      return;
    }

    try {
      const allocationData = {
        stock_management: parseInt(stockId),
        project: parseInt(allocationForm.project),
        quantity: allocationForm.quantity,
        allocation_type: allocationForm.allocation_type,
        allocated_date: allocationForm.allocated_date,
        transferred_date: allocationForm.transferred_date,
        notes: allocationForm.notes,
      };

      await createAllocation(allocationData).unwrap();
      refetch();
      toggleAllocateModal();
      alert("Allocation created successfully!");
    } catch (error) {
      console.error("Failed to create allocation:", error);
      alert(
        (error as any)?.data?.quantity?.[0] ||
          (error as any)?.data?.project?.[0] ||
          (error as any)?.data?.error ||
          "Failed to create allocation. Please try again."
      );
    }
  };

  const handleUpdateNotes = async () => {
    if (!stockId) return;

    try {
      await updateStock({
        id: stockId,
        data: { notes: notesForm.notes },
      }).unwrap();
      refetch();
      toggleUpdateNotesModal();
      alert("Notes updated successfully!");
    } catch (error) {
      console.error("Failed to update notes:", error);
      alert("Failed to update notes. Please try again.");
    }
  };

  const calculateAllocatedQuantity = () => {
    if (!stockDetail?.allocations) return 0;
    return stockDetail.allocations.reduce(
      (sum: number, alloc: { quantity: string }) => {
        return sum + parseFloat(alloc.quantity);
      },
      0
    );
  };

  const getAllocationBadgeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case "allocated":
        return "primary";
      case "transferred":
        return "success";
      case "returned":
        return "warning";
      case "consumed":
        return "info";
      case "damaged":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "success";
      case "partially_allocated":
        return "warning";
      case "fully_allocated":
        return "info";
      case "consumed":
        return "secondary";
      default:
        return "primary";
    }
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-SG", {
      style: "currency",
      currency: stockDetail?.currency || "SGD",
      minimumFractionDigits: 2,
    }).format(num);
  };

  // Timeline/Tree view component for allocations
  const AllocationTimeline = () => {
    if (!stockDetail?.allocations || stockDetail.allocations.length === 0) {
      return (
        <div className="text-center py-4">
          <i className="ri-exchange-box-line display-4 text-muted opacity-50"></i>
          <h5 className="mt-3 text-muted">No Allocations Yet</h5>
          <p className="text-muted">
            No allocations have been made for this stock item.
          </p>
          {IsValidPermissions(["ADD_STOCK_ALLOCATION"]) && (
            <Button color="primary" onClick={toggleAllocateModal}>
              <i className="ri-add-line align-middle me-1"></i>
              Create First Allocation
            </Button>
          )}
        </div>
      );
    }

    const allocations = [...stockDetail.allocations].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return (
      <div className="timeline-2">
        {allocations.map((allocation, index) => (
          <div key={allocation.id} className="timeline-item">
            <div className="timeline-item-wrapper">
              {/* Timeline line connector */}
              {index !== allocations.length - 1 && (
                <div className="timeline-connector"></div>
              )}

              {/* Timeline dot */}
              <div
                className={`timeline-dot timeline-dot-${getAllocationBadgeColor(
                  allocation.allocation_type
                )}`}
              >
                <i
                  className={`ri-${getAllocationIcon(
                    allocation.allocation_type
                  )}-fill`}
                ></i>
              </div>

              {/* Timeline content */}
              <div className="timeline-content">
                <Card className="shadow-sm border-0 mb-3">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <Badge
                            color={getAllocationBadgeColor(
                              allocation.allocation_type
                            )}
                            className="fs-11"
                          >
                            {allocation.allocation_type_display}
                          </Badge>
                          <span className="text-muted fs-12">
                            <i className="ri-calendar-line me-1"></i>
                            {new Date(
                              allocation.created_at
                            ).toLocaleDateString()}
                          </span>
                        </div>
                        <h6 className="mb-0">
                          <i className="ri-projector-line me-2 text-primary"></i>
                          {allocation.project_name}
                        </h6>
                        <p className="text-muted mb-0 fs-12">
                          Project ID: {allocation.project}
                        </p>
                      </div>
                      <div className="text-end">
                        <div className="fs-14 fw-semibold text-primary">
                          {allocation.quantity} units
                        </div>
                        <small className="text-muted">Allocated Quantity</small>
                      </div>
                    </div>

                    {/* Allocation details in grid */}
                    <Row className="g-2 mb-3">
                      <Col sm={6} md={3}>
                        <div className="border rounded p-2">
                          <small className="text-muted d-block">
                            Allocated Date
                          </small>
                          <div className="fw-semibold">
                            {new Date(
                              allocation.allocated_date
                            ).toLocaleDateString()}
                          </div>
                        </div>
                      </Col>
                      <Col sm={6} md={3}>
                        <div className="border rounded p-2">
                          <small className="text-muted d-block">
                            Transferred Date
                          </small>
                          <div className="fw-semibold">
                            {allocation.transferred_date ? (
                              new Date(
                                allocation.transferred_date
                              ).toLocaleDateString()
                            ) : (
                              <span className="text-muted">
                                Not transferred
                              </span>
                            )}
                          </div>
                        </div>
                      </Col>
                      <Col sm={6} md={3}>
                        <div className="border rounded p-2">
                          <small className="text-muted d-block">
                            Performed By
                          </small>
                          <div className="fw-semibold">
                            {allocation.performed_by_name}
                          </div>
                        </div>
                      </Col>
                      <Col sm={6} md={3}>
                        <div className="border rounded p-2">
                          <small className="text-muted d-block">
                            Reference
                          </small>
                          <div className="fw-semibold">
                            {allocation.stock_reference_number || "N/A"}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Notes section */}
                    {allocation.notes && (
                      <div className="alert alert-light bg-light border mb-0">
                        <div className="d-flex align-items-start">
                          <i className="ri-sticky-note-line text-warning me-2 mt-1"></i>
                          <div>
                            <small className="text-muted d-block">Notes</small>
                            <p className="mb-0">{allocation.notes}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Timeline footer */}
                    <div className="mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <i className="ri-time-line me-1"></i>
                          Created:{" "}
                          {new Date(allocation.created_at).toLocaleString()}
                        </small>
                        <small className="text-muted">
                          {allocation.id ? `ID: ${allocation.id}` : ""}
                        </small>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          </div>
        ))}

        {/* Timeline start/end markers */}
        <div className="timeline-start">
          <div className="timeline-start-marker">
            <i className="ri-play-circle-fill text-success"></i>
            <span className="timeline-start-text">Allocations Timeline</span>
          </div>
        </div>

        <div className="timeline-end">
          <div className="timeline-end-marker">
            <i className="ri-stop-circle-fill text-danger"></i>
            <span className="timeline-end-text">End of Timeline</span>
          </div>
        </div>
      </div>
    );
  };

  const getAllocationIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "allocated":
        return "arrow-right-up";
      case "transferred":
        return "exchange";
      case "returned":
        return "arrow-left-down";
      case "consumed":
        return "flashlight";
      case "damaged":
        return "close-circle";
      default:
        return "box-3";
    }
  };

  // Loading and error states
  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2">Loading stock details...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            <i className="ri-error-warning-line align-middle me-2"></i>
            Error loading stock management details.
          </Alert>
          <Button color="primary" onClick={() => navigate(-1)}>
            <i className="ri-arrow-left-line align-middle me-1"></i>
            Go Back
          </Button>
        </Container>
      </div>
    );
  }

  if (!stockDetail) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="warning">
            <i className="ri-alert-line align-middle me-2"></i>
            Stock item not found.
          </Alert>
        </Container>
      </div>
    );
  }

  const allocatedQuantity = calculateAllocatedQuantity();
  const actualQuantity = parseFloat(stockDetail.actual_quantity);
  const balanceQuantity = parseFloat(stockDetail.balance_quantity);
  const allocationPercentage =
    actualQuantity > 0 ? (allocatedQuantity / actualQuantity) * 100 : 0;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Stock Management Details" pageTitle="Inventory" />

        <Row className="mb-3">
          <Col lg={12}>
            <Card>
              <CardHeader className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title mb-0">
                      {stockDetail.product_name}
                    </h4>
                    <p className="text-muted mb-0">
                      Stock Reference:{" "}
                      {stockDetail.stock_reference_number || "N/A"}
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Badge
                      color={getStatusBadgeColor(stockDetail.status)}
                      className="fs-12"
                    >
                      {stockDetail.status_display}
                    </Badge>

                    {/* <Dropdown
                      isOpen={actionDropdownOpen}
                      toggle={toggleActionDropdown}
                    >
                      <DropdownToggle color="light" caret>
                        <i className="ri-more-2-fill align-middle"></i>
                      </DropdownToggle>
                      <DropdownMenu end>
                        {IsValidPermissions(["ADD_STOCK_ALLOCATION"]) && (
                          <DropdownItem onClick={toggleAllocateModal}>
                            <i className="ri-add-line align-middle me-2"></i>
                            Allocate Stock
                          </DropdownItem>
                        )}
                        {IsValidPermissions(["EDIT_STOCK_MANAGEMENT"]) && (
                          <DropdownItem onClick={toggleUpdateNotesModal}>
                            <i className="ri-edit-line align-middle me-2"></i>
                            Update Notes
                          </DropdownItem>
                        )}
                        <DropdownItem divider />
                        <DropdownItem
                          tag="a"
                          href={`/purchase-requests/${stockDetail.source_purchase_request}`}
                          target="_blank"
                        >
                          <i className="ri-external-link-line align-middle me-2"></i>
                          View Source Purchase Request
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown> */}
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                {/* Stock Summary Cards */}
                <Row className="mb-4">
                  <Col md={3}>
                    <Card className="card-animate border border-primary-subtle">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <p className="text-muted mb-1">Total Quantity</p>
                            <h4 className="mb-0 text-primary">
                              {stockDetail.actual_quantity}
                            </h4>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="avatar-sm">
                              <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                                <i className="ri-checkbox-multiple-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={3}>
                    <Card className="card-animate border border-success-subtle">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <p className="text-muted mb-1">Balance Quantity</p>
                            <h4 className="mb-0 text-success">
                              {stockDetail.balance_quantity}
                            </h4>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="avatar-sm">
                              <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                                <i className="ri-checkbox-circle-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={3}>
                    <Card className="card-animate border border-warning-subtle">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <p className="text-muted mb-1">
                              Allocated Quantity
                            </p>
                            <h4 className="mb-0 text-warning">
                              {allocatedQuantity.toFixed(2)}
                            </h4>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="avatar-sm">
                              <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                                <i className="ri-exchange-box-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={3}>
                    <Card className="card-animate border border-info-subtle">
                      <CardBody className="p-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1">
                            <p className="text-muted mb-1">Total Value</p>
                            <h4 className="mb-0 text-info">
                              {formatCurrency(stockDetail.total_cost)}
                            </h4>
                            <small className="text-muted">
                              {stockDetail.unit_price} per unit
                            </small>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="avatar-sm">
                              <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                                <i className="ri-money-dollar-circle-line"></i>
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold">Allocation Progress</span>
                    <span className="text-muted">
                      {allocationPercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="progress" style={{ height: "6px" }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${allocationPercentage}%` }}
                      aria-valuenow={allocationPercentage}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between mt-1">
                    <small className="text-muted">
                      Available: {balanceQuantity}
                    </small>
                    <small className="text-muted">
                      Allocated: {allocatedQuantity}
                    </small>
                    <small className="text-muted">
                      Total: {actualQuantity}
                    </small>
                  </div>
                </div>

                {/* Stock Details */}
                <Row className="mb-4">
                  <Col lg={12}>
                    <h5 className="mb-3">Stock Information</h5>
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Supplier
                          </label>
                          <p className="text-muted mb-0">
                            {stockDetail.supplier_name}
                          </p>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Source Project
                          </label>
                          <p className="text-muted mb-0">
                            {stockDetail.source_project_name}
                          </p>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Stock Type
                          </label>
                          <p className="text-muted mb-0">
                            <Badge color="light" className="text-dark">
                              {stockDetail.stock_type_display}
                            </Badge>
                          </p>
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Source PR Number
                          </label>
                          <p className="text-muted mb-0">
                            <Link
                              to={`/purchase-orderdetail-list/${stockDetail.source_purchase_request}/`}
                              className="text-decoration-underline"
                            >
                              {stockDetail.source_purchase_request_number}
                            </Link>
                          </p>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Created Date
                          </label>
                          <p className="text-muted mb-0">
                            {new Date(stockDetail.created_at).toLocaleString()}
                          </p>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Last Updated
                          </label>
                          <p className="text-muted mb-0">
                            {new Date(stockDetail.modified_at).toLocaleString()}
                          </p>
                        </div>
                      </Col>
                    </Row>

                    {stockDetail.notes && (
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Notes</label>
                        <div className="alert alert-light">
                          <p className="mb-0">{stockDetail.notes}</p>
                        </div>
                      </div>
                    )}

                    {Array.isArray(stockDetail.attachments) &&
                      stockDetail.attachments.length > 0 && (
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Attachments
                          </label>
                          <div className="d-flex flex-column gap-2">
                            {stockDetail.attachments.map((att: any) => (
                              <div
                                key={att.id}
                                className="d-flex align-items-center justify-content-between border rounded p-2"
                              >
                                <span className="text-truncate">
                                  <i className="ri-attachment-2 align-middle me-2"></i>
                                  {att.name || `File ${att.id}`}
                                </span>
                                {att.file_url && (
                                  <a
                                    href={att.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    <i className="ri-download-2-line align-middle me-1"></i>
                                    Download
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* Tabs */}
                <Nav tabs className="nav-tabs-custom nav-justified mb-3">
                  <NavItem>
                    <NavLink
                      className={activeTab === "1" ? "active" : ""}
                      onClick={() => setActiveTab("1")}
                    >
                      <i className="ri-history-line align-middle me-2"></i>
                      Allocation Timeline
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="ri-table-line align-middle me-2"></i>
                      Allocation Summary
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  {/* Timeline Tab */}
                  <TabPane tabId="1">
                    <Card className="border-0 shadow-sm">
                      <CardHeader className="bg-light border-bottom d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">
                          <i className="ri-history-line align-middle me-2"></i>
                          Allocation Timeline
                        </h5>
                        {IsValidPermissions(["ADD_STOCK_ALLOCATION"]) && (
                          <Button
                            color="primary"
                            onClick={toggleAllocateModal}
                            className="rounded-pill"
                          >
                            <i className="ri-add-line align-middle me-1"></i>
                            Allocate Stock
                          </Button>
                        )}
                      </CardHeader>
                      <CardBody>
                        <AllocationTimeline />
                      </CardBody>
                    </Card>
                  </TabPane>

                  {/* Summary Tab */}
                  <TabPane tabId="2">
                    <Card className="border-0 shadow-sm">
                      <CardHeader className="bg-light border-bottom">
                        <h5 className="card-title mb-0">
                          <i className="ri-table-line align-middle me-2"></i>
                          Allocation Summary
                        </h5>
                      </CardHeader>
                      <CardBody>
                        {stockDetail.allocations &&
                        stockDetail.allocations.length > 0 ? (
                          <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Target Project</th>
                                  <th>PO Number</th>
                                  <th className="text-center">Quantity</th>
                                  <th className="text-center">
                                    Allocation Type
                                  </th>
                                  <th className="text-center">Transfer Status</th>
                                  <th>Allocated Date</th>
                                  <th>Transferred Date</th>
                                  <th>Performed By</th>
                                  <th>Notes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {stockDetail.allocations.map(
                                  (allocation: any) => (
                                    <tr key={allocation.id}>
                                      <td>
                                        <div className="fw-semibold">
                                          {allocation.target_project_name ||
                                            allocation.project_name ||
                                            "—"}
                                        </div>
                                        {!!allocation.project && (
                                          <small className="text-muted">
                                            ID: {allocation.project}
                                          </small>
                                        )}
                                      </td>
                                      <td>
                                        {allocation.purchase_order_number ? (
                                          <Badge color="primary">
                                            {allocation.purchase_order_number}
                                          </Badge>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                      <td className="text-center">
                                        <span className="fw-bold">
                                          {allocation.quantity}
                                        </span>
                                      </td>
                                      <td className="text-center">
                                        <Badge
                                          color={getAllocationBadgeColor(
                                            allocation.allocation_type
                                          )}
                                        >
                                          {allocation.allocation_type_display}
                                        </Badge>
                                      </td>
                                      <td className="text-center">
                                        {allocation.transfer_status ? (
                                          <Badge color="info">
                                            {allocation.transfer_status}
                                          </Badge>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                      <td>
                                        {new Date(
                                          allocation.allocated_date
                                        ).toLocaleDateString()}
                                      </td>
                                      <td>
                                        {allocation.transferred_date
                                          ? new Date(
                                              allocation.transferred_date
                                            ).toLocaleDateString()
                                          : "—"}
                                      </td>
                                      <td>{allocation.performed_by_name}</td>
                                      <td>
                                        <small
                                          className="text-truncate d-block"
                                          style={{ maxWidth: "200px" }}
                                        >
                                          {allocation.notes || "—"}
                                        </small>
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                              <tfoot className="table-light">
                                <tr>
                                  <th colSpan={2} className="text-end">
                                    Total Allocated:
                                  </th>
                                  <th className="text-center">
                                    {allocatedQuantity.toFixed(2)}
                                  </th>
                                  <th colSpan={6}></th>
                                </tr>
                              </tfoot>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <i className="ri-table-line display-4 text-muted opacity-50"></i>
                            <h5 className="mt-3 text-muted">
                              No Allocation Data
                            </h5>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Allocate Stock Modal */}
      <Modal isOpen={allocateModal} toggle={toggleAllocateModal} centered>
        <ModalHeader toggle={toggleAllocateModal}>
          Allocate Stock to Project
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p>
              Product: <strong>{stockDetail.product_name}</strong>
              <br />
              Available Balance: <strong>{stockDetail.balance_quantity}</strong>
            </p>
          </div>
          <Form>
            <FormGroup className="mb-3">
              <Label for="project">
                Project <span className="text-danger">*</span>
              </Label>
              <Input
                type="select"
                id="project"
                name="project"
                value={allocationForm.project}
                onChange={handleAllocationInputChange}
                required
              >
                <option value="">Select Project</option>
                {/* You would populate this from your projects API */}
                <option value="4">KCDE Project</option>
                <option value="3">Tengah Project</option>
              </Input>
            </FormGroup>

            {selectedProjectId && isLoadingProjectCapacity && (
              <div className="text-muted small mb-3">
                Checking target project product capacity...
              </div>
            )}
            {selectedProjectId && projectCapacityError && (
              <Alert color="danger" className="py-2 mb-3">
                {(projectCapacityError as any)?.data?.error ||
                  "This product is not assigned and published for the target project."}
              </Alert>
            )}
            {selectedProjectId && projectCapacity && (
              <Alert color="info" className="py-2 mb-3">
                Target project maximum: <strong>{(projectCapacity as any).maximum_quantity}</strong>
                {" | "}Already requested: <strong>{(projectCapacity as any).requested_quantity}</strong>
                {" | "}Remaining: <strong>{(projectCapacity as any).remaining_quantity}</strong>
              </Alert>
            )}

            <FormGroup className="mb-3">
              <Label for="quantity">
                Quantity <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                id="quantity"
                name="quantity"
                value={allocationForm.quantity}
                onChange={handleAllocationInputChange}
                placeholder="Enter quantity"
                min="0.01"
                step="0.01"
                max={maximumTransferQuantity}
                required
              />
              <div className="form-text">
                Maximum transfer: {maximumTransferQuantity} units
                {hasTargetCapacity &&
                  ` (limited by the target project's remaining quantity)`}
              </div>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="allocation_type">Allocation Type</Label>
              <Input
                type="select"
                id="allocation_type"
                name="allocation_type"
                value={allocationForm.allocation_type}
                onChange={handleAllocationInputChange}
              >
                <option value="allocated">Allocated</option>
                <option value="transferred">Transferred</option>
                <option value="returned">Returned</option>
                <option value="consumed">Consumed</option>
                <option value="damaged">Damaged</option>
              </Input>
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="allocated_date">Allocated Date</Label>
                  <Input
                    type="date"
                    id="allocated_date"
                    name="allocated_date"
                    value={allocationForm.allocated_date}
                    onChange={handleAllocationInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-3">
                  <Label for="transferred_date">Transferred Date</Label>
                  <Input
                    type="date"
                    id="transferred_date"
                    name="transferred_date"
                    value={allocationForm.transferred_date}
                    onChange={handleAllocationInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-3">
              <Label for="notes">Notes</Label>
              <Input
                type="textarea"
                id="notes"
                name="notes"
                value={allocationForm.notes}
                onChange={handleAllocationInputChange}
                rows={3}
                placeholder="Enter allocation notes"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleAllocateModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleCreateAllocation}
            disabled={
              !allocationForm.project ||
              !allocationForm.quantity ||
              isCreatingAllocation ||
              isLoadingProjectCapacity ||
              !!projectCapacityError ||
              Number(allocationForm.quantity) > maximumTransferQuantity
            }
          >
            {isCreatingAllocation ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Allocating...
              </>
            ) : (
              <>
                <i className="ri-check-line align-middle me-1" />
                Allocate Stock
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Update Notes Modal */}
      {/* <Modal isOpen={updateNotesModal} toggle={toggleUpdateNotesModal} centered>
        <ModalHeader toggle={toggleUpdateNotesModal}>
          Update Stock Notes
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup className="mb-3">
              <Label for="notes">Notes</Label>
              <Input
                type="textarea"
                id="notes"
                name="notes"
                value={notesForm.notes}
                onChange={handleNotesInputChange}
                rows={5}
                placeholder="Enter notes about this stock item..."
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleUpdateNotesModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleUpdateNotes}
            disabled={isUpdatingStock}
          >
            {isUpdatingStock ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Updating...
              </>
            ) : (
              <>
                <i className="ri-save-line align-middle me-1" />
                Update Notes
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal> */}

      {/* Add CSS for timeline */}
      <style>{`
        .timeline-2 {
          position: relative;
          padding: 20px 0;
        }
        
        .timeline-item {
          position: relative;
          padding-left: 30px;
          margin-bottom: 30px;
        }
        
        .timeline-item-wrapper {
          position: relative;
        }
        
        .timeline-connector {
          position: absolute;
          left: 9px;
          top: 24px;
          bottom: -30px;
          width: 2px;
          background-color: #e9ecef;
          z-index: 1;
        }
        
        .timeline-dot {
          position: absolute;
          left: 0;
          top: 0;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          border: 3px solid white;
        }
        
        .timeline-dot-primary {
          background-color: #405189;
          color: white;
        }
        
        .timeline-dot-success {
          background-color: #0ab39c;
          color: white;
        }
        
        .timeline-dot-warning {
          background-color: #f7b84b;
          color: white;
        }
        
        .timeline-dot-info {
          background-color: #299cdb;
          color: white;
        }
        
        .timeline-dot-danger {
          background-color: #f06548;
          color: white;
        }
        
        .timeline-dot-secondary {
          background-color: #878a99;
          color: white;
        }
        
        .timeline-content {
          margin-left: 30px;
        }
        
        .timeline-start,
        .timeline-end {
          text-align: center;
          margin: 20px 0;
          position: relative;
        }
        
        .timeline-start-marker,
        .timeline-end-marker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background-color: #f8f9fa;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .timeline-start-marker {
          color: #0ab39c;
          border: 1px solid #d4edda;
        }
        
        .timeline-end-marker {
          color: #f06548;
          border: 1px solid #f8d7da;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .timeline-item {
            padding-left: 20px;
          }
          
          .timeline-connector {
            left: 9px;
          }
          
          .timeline-content {
            margin-left: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default StockManagementDetailView;
